import React, { useEffect, useRef } from 'react'
import type { OpeningChildDto } from '@chessx/shared/opening-tree'
import CopyChip from './CopyChip'
import { moveNumberLabel, toPgn } from '../pgn'
import { lineKey } from '../useOpeningTree'

export interface OpeningTreeNodeProps {
  node: OpeningChildDto
  ply: number
  depth: number
  line: readonly string[]
  childrenByLine: Record<string, OpeningChildDto[]>
  expandedLines: ReadonlySet<string>
  loadingLines: ReadonlySet<string>
  selectedLine: readonly string[]
  selectedFen: string | null
  namedOnly: boolean
  onToggle: (fen: string, line: readonly string[]) => void
  onSelect: (fen: string, line: readonly string[]) => void
  onRetry: (fen: string, line: readonly string[]) => void
}

const SKELETON_ROWS = 3

const OpeningTreeNode = ({
  node,
  ply,
  depth,
  line,
  childrenByLine,
  expandedLines,
  loadingLines,
  selectedLine,
  selectedFen,
  namedOnly,
  onToggle,
  onSelect,
  onRetry
}: OpeningTreeNodeProps) => {
  const rowRef = useRef<HTMLDivElement>(null)
  const key = lineKey(line)
  const isExpanded = expandedLines.has(key)
  const isLoading = loadingLines.has(key)
  const isSelected = selectedFen === node.fen && lineKey(selectedLine) === key
  const children = childrenByLine[key]
  const visibleChildren = namedOnly
    ? children?.filter(child => child.openingName !== null)
    : children

  useEffect(() => {
    if (isSelected) rowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [isSelected])

  const handleActivate = () => {
    onSelect(node.fen, line)
    if (node.hasChildren && !isExpanded) onToggle(node.fen, line)
  }

  const pgn = toPgn(line)

  return (
    <div className="otree-node">
      <div
        ref={rowRef}
        className={`otree-node-row ${isSelected ? 'is-selected' : ''}`}
        style={{ paddingLeft: `${depth * 18 + 10}px` }}
      >
        <button
          className={`otree-chevron ${node.hasChildren ? 'has-children' : 'no-children'} ${isExpanded && !isLoading ? 'open' : ''}`}
          onClick={event => {
            event.stopPropagation()
            if (node.hasChildren) onToggle(node.fen, line)
          }}
          tabIndex={node.hasChildren ? 0 : -1}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          aria-busy={isLoading}
          type="button"
        >
          {node.hasChildren &&
            (isLoading ? (
              <span className="otree-chevron-spinner" />
            ) : (
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            ))}
        </button>

        <div className="otree-node-content" onClick={handleActivate}>
          <div className="otree-node-headline">
            <span className="otree-move-badge">{moveNumberLabel(node.san, ply)}</span>
            <span className={`otree-node-name ${node.openingName ? '' : 'is-unnamed'}`}>
              {node.openingName ?? 'unnamed position'}
            </span>
            {node.maxDepth > 0 && (
              <span
                className="otree-depth-badge"
                title="Longest line remaining below this position"
              >
                +{node.maxDepth} ply
              </span>
            )}
          </div>

          {isSelected && (
            <div className="otree-node-detail">
              <CopyChip label="PGN" value={pgn} />
              <CopyChip label="FEN" value={node.fen} />
            </div>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="otree-children">
          {isLoading && (
            <div className="otree-loading" aria-label="Loading continuations">
              {[...Array(SKELETON_ROWS)].map((_, index) => (
                <div key={index} className="otree-skel-row">
                  <div className="otree-skel-line w25" />
                  <div className="otree-skel-line w40" />
                </div>
              ))}
            </div>
          )}
          {!isLoading && children === undefined && (
            <div className="otree-inline-loading">
              Couldn’t load continuations.
              <button
                className="otree-inline-retry"
                type="button"
                onClick={() => onRetry(node.fen, line)}
              >
                Retry
              </button>
            </div>
          )}
          {visibleChildren?.length === 0 && !isLoading && (
            <div className="otree-inline-loading">No named continuations</div>
          )}
          {visibleChildren?.map(child => (
            <OpeningTreeNode
              key={child.san}
              node={child}
              ply={ply + 1}
              depth={depth + 1}
              line={[...line, child.san]}
              childrenByLine={childrenByLine}
              expandedLines={expandedLines}
              loadingLines={loadingLines}
              selectedLine={selectedLine}
              selectedFen={selectedFen}
              namedOnly={namedOnly}
              onToggle={onToggle}
              onSelect={onSelect}
              onRetry={onRetry}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default OpeningTreeNode
